/*
 * MutationObserver helpers for the habitat module.
 *
 * observeMounts(root, selector, onMount)
 *   Watches `root.body` for added nodes matching `selector`; calls
 *   `onMount(el)` for each matched node (including matched descendants
 *   of newly-added subtrees). Returns `{ disconnect() }`.
 *
 * observeTreeMutations(root, onMutation)
 *   Watches `root.body` for childList mutations anywhere in the tree
 *   and calls `onMutation()` (without args) on every batch. This is the low-level
 *   hook used by the habitat module to detect DOM-level changes that may
 *   indicate mounted elements were detached. The callback is expected to
 *   iterate its own tracked set (hence "tree mutations" rather than per-element
 *   detachment) — this lets callers share one observer across many tracked
 *   elements instead of installing one observer per element.
 *
 * Both return `{ disconnect() }`. `root` is a Document (e.g. `document` or
 * an iframe's `contentDocument`); MutationObserver does not cross iframe
 * boundaries, so a caller that wants to observe content inside an iframe
 * must pass that iframe's document explicitly.
 */

const findMatches = ( nodes, selector ) => {
	const results = [];
	for ( let i = 0; i < nodes.length; i++ ) {
		const node = nodes[ i ];
		if ( node.nodeType !== 1 ) continue; // ELEMENT_NODE
		if ( node.matches && node.matches( selector ) ) results.push( node );
		if ( node.querySelectorAll ) {
			const descendants = node.querySelectorAll( selector );
			for ( let j = 0; j < descendants.length; j++ ) {
				results.push( descendants[ j ] );
			}
		}
	}
	return results;
};

const observeMounts = ( root, selector, onMount ) => {
	let disposed = false;
	const win = root.defaultView || window;

	const observer = new win.MutationObserver( ( mutations ) => {
		if ( disposed ) return;
		for ( const mutation of mutations ) {
			if ( mutation.type !== 'childList' ) continue;
			const matches = findMatches( mutation.addedNodes, selector );
			for ( const el of matches ) {
				if ( disposed ) return;
				onMount( el );
			}
		}
	} );

	observer.observe( root.body, { childList: true, subtree: true } );

	return {
		disconnect() {
			disposed = true;
			observer.disconnect();
		},
	};
};

const observeTreeMutations = ( root, onMutation ) => {
	let disposed = false;
	const win = root.defaultView || window;

	const observer = new win.MutationObserver( () => {
		if ( disposed ) return;
		onMutation();
	} );

	observer.observe( root.body, { childList: true, subtree: true } );

	return {
		disconnect() {
			disposed = true;
			observer.disconnect();
		},
	};
};

export { observeMounts, observeTreeMutations };
